import { Link } from "react-router-dom";
import { featuredMovies } from "@/mocks/movies";
import { featuredTopics, categoryInfo } from "@/mocks/catalog";
import { latestMovies, latestNews } from "@/utils/data";
import { usePageMeta } from "@/hooks/usePageMeta";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntries from "./components/CategoryEntries";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";

export default function Home() {
  usePageMeta({
    title: "黄色在线-热门影视资源与最新影视资讯平台",
    description:
      "黄色在线为用户提供丰富的影视资源和最新影视资讯，涵盖电影、电视剧、综艺等多种类别，方便用户浏览和了解影视动态。",
    keywords: "黄色在线,影视资源,电影,电视剧,影视资讯,在线影视,热门影视",
  });

  const hotMovies = latestMovies(8);
  const hotNews = latestNews(6);

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="w-full bg-primary-900">
        <div className="grid w-full grid-cols-1 items-center gap-8 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-2 lg:gap-12">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-300">
              <i className="ri-fire-line" aria-hidden="true" />
              每日更新 · 热门影视与行业资讯
            </span>
            <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              热门影视资源
              <br />
              与最新影视资讯平台
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-300 md:text-base">
              黄色在线聚合电影、电视剧、综艺等热门作品，提供分类筛选、关键词搜索与作品详情，
              让你快速找到想看的内容，第一时间了解影视行业动态。
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/category/movie"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent-600"
              >
                <i className="ri-compass-3-line text-lg" aria-hidden="true" />
                开始浏览影视
              </Link>
              <Link
                to="/news/industry"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-5 py-2.5 text-sm font-medium text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                <i className="ri-newspaper-line text-lg" aria-hidden="true" />
                查看影视资讯
              </Link>
            </div>
            <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-primary-700/70 pt-6">
              <div>
                <dt className="text-xs text-background-400">收录作品</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-accent-300">1,200+</dd>
              </div>
              <div>
                <dt className="text-xs text-background-400">影视分类</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-accent-300">3 大类</dd>
              </div>
              <div>
                <dt className="text-xs text-background-400">每日资讯</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-accent-300">实时更新</dd>
              </div>
            </dl>
          </div>

          <div className="animate-fade-in">
            <HeroCarousel items={featuredMovies} />
          </div>
        </div>
      </section>

      {/* Category quick entries */}
      <section className="w-full px-4 py-10 md:px-8 md:py-12">
        <SectionHeading
          title="影视分类快捷入口"
          subtitle="按类别快速找到你感兴趣的内容"
        />
        <CategoryEntries />
      </section>

      {/* Two-column: latest movies + sidebar */}
      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHeading
              title="最新影视资源"
              subtitle="按更新时间排序的热门作品"
              moreTo="/category/movie"
            />
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <SectionHeading title="影视速览" subtitle="按分类直达" />
            <div className="flex flex-col gap-3">
              {categoryInfo.map((cat) => (
                <Link
                  key={cat.key}
                  to={`/category/${cat.key}`}
                  className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-50 px-4 py-3 transition-colors hover:border-accent-300 hover:bg-accent-50/60"
                >
                  <span className="flex items-center gap-3">
                    <i className={`${cat.icon} text-lg text-primary-600`} aria-hidden="true" />
                    <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </Link>
              ))}
            </div>

            <div className="mt-8">
              <SectionHeading title="资讯热榜" subtitle="大家正在看" />
              <ol className="flex flex-col gap-1">
                {hotNews.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="group flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-background-200/60"
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md font-heading text-xs font-bold ${
                          i < 3
                            ? "bg-accent-500 text-white"
                            : "bg-secondary-200 text-secondary-700"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-sm font-medium leading-snug text-foreground-800 transition-colors group-hover:text-accent-600">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">
                          {item.publishDate}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>

      {/* Latest news */}
      <section className="w-full px-4 py-10 md:px-8 md:py-12">
        <SectionHeading
          title="最新影视资讯"
          subtitle="行业动态、影视评论与幕后花絮"
          moreTo="/news/industry"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {hotNews.slice(0, 3).map((item) => (
            <NewsCard key={item.id} item={item} variant="grid" />
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4">
          {hotNews.slice(3).map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Featured topics */}
      <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-12">
        <SectionHeading title="编辑精选专题" subtitle="围绕主题的系列内容合集" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {featuredTopics.map((topic) => (
            <article
              key={topic.id}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-accent-300"
            >
              <div className="h-44 w-full overflow-hidden bg-secondary-200">
                <img
                  src={topic.cover}
                  alt={topic.title}
                  title={`${topic.title} - 黄色在线专题`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <span className="inline-flex rounded-md bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
                  {topic.tag}
                </span>
                <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">
                  {topic.title}
                </h3>
                <p className="mt-1 text-sm font-medium text-primary-600">{topic.subtitle}</p>
                <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-500">
                  {topic.desc}
                </p>
                <Link
                  to="/search?q=精选"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent-600 transition-colors hover:text-accent-700"
                >
                  查看专题内容
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

